import clsx from 'clsx';

interface VoiceMicButtonProps {
  isRecording: boolean;
  onToggle: () => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

export function VoiceMicButton({
  isRecording,
  onToggle,
  size = 'medium',
  disabled = false,
}: VoiceMicButtonProps) {
  const sizeClasses = {
    small: 'w-12 h-12',
    medium: 'w-16 h-16',
    large: 'w-24 h-24',
  };

  const iconSizes = {
    small: 'w-6 h-6',
    medium: 'w-8 h-8',
    large: 'w-12 h-12',
  };

  return (
    <div className="flex flex-col items-center">
      <button
        onClick={onToggle}
        disabled={disabled}
        aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        className={clsx(
          'relative rounded-full flex items-center justify-center transition-all focus:outline-none focus:ring-4',
          sizeClasses[size],
          isRecording
            ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 hover:bg-red-600 focus:ring-red-200'
            : 'bg-primary-500 text-white shadow-lg shadow-primary-500/30 hover:bg-primary-600 focus:ring-primary-200',
          disabled && 'opacity-50 cursor-not-allowed hover:bg-primary-500'
        )}
      >
        {/* Pulsing ring when recording */}
        {isRecording && !disabled && (
          <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-25" />
        )}

        {/* Icon */}
        {isRecording ? (
          <StopIcon className={iconSizes[size]} />
        ) : (
          <MicIcon className={iconSizes[size]} />
        )}
      </button>

      {/* Status text */}
      {!disabled && (
        <p className="mt-3 text-sm text-gray-500">
          {isRecording ? 'Tap to stop' : 'Tap to record'}
        </p>
      )}
    </div>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

function StopIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}
